import { create } from 'zustand';

/**
 * Histórico recente de comandos enviados, por embarcação, com correlação ao
 * evento `commandResult` que volta do equipamento. Não persiste em
 * localStorage porque é estado efêmero (perdeu reload, perdeu).
 */
export type CommandStatus = 'sent' | 'confirmed' | 'failed' | 'timeout';

export interface CommandRecord {
  /** id local */
  uid: string;
  deviceId: number;
  /** nome amigável: "Bloquear", "Desbloquear", etc */
  label: string;
  /** comando cru enviado: "RELAY,1#", etc */
  data: string;
  /** ms epoch */
  sentAt: number;
  status: CommandStatus;
  /** resposta crua do equipamento (se houver) */
  result?: string;
  /** id do evento commandResult que confirmou (se houver) */
  resultEventId?: number;
}

const TIMEOUT_MS = 3 * 60 * 1000; // 3 min

interface CommandsState {
  records: CommandRecord[];
  push: (record: Omit<CommandRecord, 'uid' | 'status'>) => string;
  confirm: (deviceId: number, eventId: number, result?: string) => void;
  fail: (uid: string, reason?: string) => void;
  pruneTimeouts: () => void;
  /**
   * Inferência: quando uma posição nova chega para um device com comando
   * pendente cujo data combina com o novo estado observado (RELAY,1# →
   * blocked=true; RELAY,0# → blocked=false; POSITION# → qualquer posição
   * posterior ao envio), marca o comando como `confirmed`.
   */
  inferConfirmFromPosition: (
    deviceId: number,
    snapshot: { blocked?: boolean; fixTime: string },
  ) => void;
  clearForDevice: (deviceId: number) => void;
}

export const useCommandsStore = create<CommandsState>((set) => ({
  records: [],
  push: (record) => {
    const uid = `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set((state) => ({
      records: [
        { ...record, uid, status: 'sent' as CommandStatus },
        ...state.records,
      ].slice(0, 50),
    }));
    return uid;
  },
  confirm: (deviceId, eventId, result) => {
    set((state) => {
      // Casa com o comando mais recente em status 'sent' do mesmo deviceId,
      // dentro da janela de TIMEOUT_MS. Como o Traccar não correlaciona
      // explicitamente o evento ao comando enviado, usamos heurística temporal.
      const now = Date.now();
      let matchedUid: string | null = null;
      for (const r of state.records) {
        if (r.deviceId !== deviceId) continue;
        if (r.status !== 'sent') continue;
        if (now - r.sentAt > TIMEOUT_MS) continue;
        matchedUid = r.uid;
        break;
      }
      if (!matchedUid) return state;
      return {
        records: state.records.map((r) =>
          r.uid === matchedUid
            ? { ...r, status: 'confirmed' as CommandStatus, result, resultEventId: eventId }
            : r,
        ),
      };
    });
  },
  fail: (uid, reason) =>
    set((state) => ({
      records: state.records.map((r) =>
        r.uid === uid ? { ...r, status: 'failed' as CommandStatus, result: reason } : r,
      ),
    })),
  pruneTimeouts: () =>
    set((state) => {
      const now = Date.now();
      let changed = false;
      const next = state.records.map((r) => {
        if (r.status === 'sent' && now - r.sentAt > TIMEOUT_MS) {
          changed = true;
          return { ...r, status: 'timeout' as CommandStatus };
        }
        return r;
      });
      return changed ? { records: next } : state;
    }),
  inferConfirmFromPosition: (deviceId, snapshot) => {
    set((state) => {
      let matchedUid: string | null = null;
      for (const r of state.records) {
        if (r.deviceId !== deviceId) continue;
        if (r.status !== 'sent') continue;
        const matchesBlock =
          (r.data === 'RELAY,1#' && snapshot.blocked === true) ||
          (r.data === 'RELAY,0#' && snapshot.blocked === false);
        // POSITION# só confirma quando a posição é posterior ao envio
        // (o efeito do comando é gerar uma nova fix).
        const fixMs = new Date(snapshot.fixTime).getTime();
        const matchesForcedPosition =
          r.data === 'POSITION#' && Number.isFinite(fixMs) && fixMs > r.sentAt;
        if (matchesBlock || matchesForcedPosition) {
          matchedUid = r.uid;
          break;
        }
      }
      if (!matchedUid) return state;
      return {
        records: state.records.map((r) =>
          r.uid === matchedUid
            ? {
                ...r,
                status: 'confirmed' as CommandStatus,
                result: 'inferido pela posição',
              }
            : r,
        ),
      };
    });
  },
  clearForDevice: (deviceId) =>
    set((state) => ({ records: state.records.filter((r) => r.deviceId !== deviceId) })),
}));
